import React from 'react';
import { Card, Col, Row, Table, Tag, Input, Typography, Grid } from 'antd';
import { useSelector } from 'react-redux';
import {
  UserOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  UserDeleteOutlined,
  ClockCircleOutlined,
  Loading3QuartersOutlined,
  CloseCircleOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MetricCard from '../components/MetricCard';
import { formatCurrency } from '../utils/formatters';

const { Title, Text } = Typography;

const Dashboard = () => {
  const navigate = useNavigate();
  const { users } = useSelector((state) => state.bde);
  const { orders } = useSelector((state) => state.orders);
  const { shops } = useSelector((state) => state.shops);
  const [query, setQuery] = React.useState('');

  const todayStr = new Date().toISOString().split('T')[0];
  const todayOrders = orders.filter(o => o.createdAt.startsWith(todayStr)).length;
  const todayOnboardedShops = shops.filter(s => s.createdAt && s.createdAt.startsWith(todayStr)).length;

  const activeBDEs = users.filter(u => u.status === 'verified').length;
  const inactiveBDEs = users.filter(u => u.status !== 'verified').length;
  const totalOrders = orders.length;
  const totalEarnings = orders.reduce((sum, order) => sum + (Number(order.amount) || 0), 0);
  const commissionToBePaid = orders
    .filter(o => !o.commissionPaid && ['Expected', 'Processing', 'Completed'].includes(o.status))
    .reduce((sum, o) => sum + ((Number(o.amount) * 0.10) || 0), 0);

  // Calculate trends and sparkline data
  const getLast14DaysStats = () => {
    const stats = [];
    const today = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];

      const dayOrders = orders.filter(o => o.createdAt.startsWith(dateStr));
      stats.push({
        date: dateStr,
        count: dayOrders.length,
        earnings: dayOrders.reduce((sum, o) => sum + (Number(o.amount) || 0), 0),
        completed: dayOrders.filter(o => o.status === 'Completed').length
      });
    }
    return stats;
  };

  const dailyStats = getLast14DaysStats();
  const currentWeekStats = dailyStats.slice(7); // Last 7 days
  const prevWeekStats = dailyStats.slice(0, 7); // Previous 7 days

  const calculateTrend = (current, prev) => {
    const currentSum = current.reduce((a, b) => a + b, 0);
    const prevSum = prev.reduce((a, b) => a + b, 0);
    if (prevSum === 0) return currentSum > 0 ? 100 : 0;
    return ((currentSum - prevSum) / prevSum * 100).toFixed(1);
  };

  const earningsTrend = calculateTrend(
    currentWeekStats.map(s => s.earnings),
    prevWeekStats.map(s => s.earnings)
  );

  const ordersTrend = calculateTrend(
    currentWeekStats.map(s => s.count),
    prevWeekStats.map(s => s.count)
  );

  const completedTrend = calculateTrend(
    currentWeekStats.map(s => s.completed),
    prevWeekStats.map(s => s.completed)
  );

  const statusCounts = orders.reduce((acc, order) => {
    acc[order.status] = (acc[order.status] || 0) + 1;
    return acc;
  }, {});

  const pendingOrders = statusCounts['Pending'] || 0;
  const expectedOrders = statusCounts['Expected'] || 0;
  const processingOrders = statusCounts['Processing'] || 0;
  const completedOrders = statusCounts['Completed'] || 0;
  const cancelledOrders = statusCounts['Cancelled'] || 0;

  const screens = Grid.useBreakpoint();

  return (
    <div style={{ paddingBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Dashboard Overview</Title>
          <Text type="secondary">Welcome back, here's what's happening today.</Text>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Tag color="blue" style={{ padding: '4px 12px', fontSize: 14 }}>{new Date().toLocaleDateString()}</Tag>
        </div>
      </div>

      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Total Earnings"
            value={formatCurrency(totalEarnings)}
            prefix={<span style={{ fontWeight: 'bold' }}>₹</span>}
            color="#2563eb"
            trend={parseFloat(earningsTrend)}
            suffix=""
            data={currentWeekStats.map(s => s.earnings)}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Total Orders"
            value={totalOrders}
            prefix={<ShoppingCartOutlined />}
            color="#8b5cf6"
            trend={parseFloat(ordersTrend)}
            data={currentWeekStats.map(s => s.count)}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Today's Orders"
            value={todayOrders}
            prefix={<ShoppingCartOutlined />}
            color="#ec4899"
            trend={null}
            data={[todayOrders, todayOrders]}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Today Onboarded Shops"
            value={todayOnboardedShops}
            prefix={<ShopOutlined />}
            color="#14b8a6"
            trend={null}
            data={[todayOnboardedShops, todayOnboardedShops]}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Active BDEs"
            value={activeBDEs}
            prefix={<UserOutlined />}
            color="#10b981"
            trend={null}
            data={[activeBDEs, activeBDEs]} // Flat line
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Completed Orders"
            value={completedOrders}
            prefix={<CheckCircleOutlined />}
            color="#f59e0b"
            trend={parseFloat(completedTrend)}
            data={currentWeekStats.map(s => s.completed)}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Commission To Be Paid"
            value={formatCurrency(commissionToBePaid)}
            prefix={<span style={{ fontWeight: 'bold' }}>₹</span>}
            color="#3f8600"
            suffix=""
            trend={null}
            data={[commissionToBePaid, commissionToBePaid]}
          />
        </Col>
      </Row>

      <Title level={4} style={{ marginBottom: 16 }}>Order Status</Title>
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={8} lg={4}>
          <MetricCard title="Pending" value={pendingOrders} prefix={<ClockCircleOutlined />} color="#f59e0b" />
        </Col>
        <Col xs={24} sm={8} lg={4}>
          <MetricCard title="Expected" value={expectedOrders} prefix={<SafetyCertificateOutlined />} color="#06b6d4" />
        </Col>
        <Col xs={24} sm={8} lg={4}>
          <MetricCard title="Processing" value={processingOrders} prefix={<Loading3QuartersOutlined />} color="#3b82f6" />
        </Col>
        <Col xs={24} sm={8} lg={4}>
          <MetricCard title="Cancelled" value={cancelledOrders} prefix={<CloseCircleOutlined />} color="#ef4444" />
        </Col>
        <Col xs={24} sm={8} lg={4}>
          <MetricCard title="Inactive BDEs" value={inactiveBDEs} prefix={<UserDeleteOutlined />} color="#64748b" />
        </Col>
      </Row>

      <Card
        bordered={false}
        bodyStyle={{ padding: 24 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
          <Title level={4} style={{ margin: 0 }}>Recent Orders</Title>
          <Input
            prefix={<SearchOutlined style={{ color: '#cbd5e1' }} />}
            placeholder="Search orders..."
            bordered={false}
            style={{ background: '#f1f5f9', borderRadius: 8, width: 250, maxWidth: '100%', padding: '6px 12px' }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {!screens.md ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[...orders]
              .filter((o) => {
                const q = query.toLowerCase().trim();
                if (!q) return true;
                return Object.values(o).some(val => String(val).toLowerCase().includes(q)) ||
                  (users.find(u => u.id === o.bdeId)?.name || '').toLowerCase().includes(q);
              })
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .slice(0, 10).map(order => { // Limit to 10 for recent
                let color = 'default';
                if (order.status === 'Pending') color = 'warning';
                if (order.status === 'Verified') color = 'cyan';
                if (order.status === 'Processing') color = 'processing';
                if (order.status === 'Completed') color = 'success';
                if (order.status === 'Cancelled') color = 'error';

                return (
                  <Card key={order.id} size="small" hoverable onClick={() => navigate(`/admin/orders/details/${order.id}`)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>#{order.id}</span>
                      <Tag color={color} style={{ margin: 0 }}>{order.status.toUpperCase()}</Tag>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Date:</span>
                        <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Client:</span>
                        <span style={{ fontWeight: 500 }}>{order.customerName}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Amount:</span>
                        <span style={{ fontWeight: 600, color: '#0f172a' }}>₹{Number(order.amount).toLocaleString()}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>BDE:</span>
                        <span>{users.find(u => u.id === order.bdeId)?.name || 'Unknown'}</span>
                      </div>
                    </div>
                  </Card>
                )
              })}
          </div>
        ) : (
          <Table
            rowKey="id"
            dataSource={[...orders]
              .filter((o) => {
                const q = query.toLowerCase().trim();
                if (!q) return true;
                return Object.values(o).some(val => String(val).toLowerCase().includes(q)) ||
                  (users.find(u => u.id === o.bdeId)?.name || '').toLowerCase().includes(q);
              })
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
            onRow={(record) => ({
              onDoubleClick: () => navigate(`/admin/orders/details/${record.id}`),
              style: { cursor: 'pointer' }
            })}
            pagination={{ pageSize: 5 }}
            columns={[
              {
                title: 'Order Date',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (text) => <span style={{ color: '#64748b' }}>{new Date(text).toLocaleDateString()}</span>,
                sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
              },
              {
                title: 'Order ID',
                dataIndex: 'id',
                key: 'id',
                render: (text) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>#{text}</span>,
              },
              {
                title: 'Client',
                dataIndex: 'customerName',
                key: 'customerName',
                render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                key: 'amount',
                render: (amount) => <span style={{ fontWeight: 600 }}>₹{Number(amount).toLocaleString()}</span>,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status) => {
                  let color = 'default';
                  if (status === 'Pending') color = 'warning';
                  if (status === 'Verified') color = 'cyan';
                  if (status === 'Processing') color = 'processing';
                  if (status === 'Completed') color = 'success';
                  if (status === 'Cancelled') color = 'error';
                  return (
                    <Tag color={color} style={{ borderRadius: 12, fontWeight: 600, border: 'none' }}>
                      {status.toUpperCase()}
                    </Tag>
                  );
                }
              },
              {
                title: 'BDE',
                key: 'bdeName',
                render: (_, record) => {
                  const bde = users.find(u => u.id === record.bdeId);
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#64748b' }}>
                        {bde?.name?.charAt(0) || 'U'}
                      </div>
                      <span>{bde?.name || 'Unknown'}</span>
                    </div>
                  );
                }
              },
            ]}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
